// Entities.jsx — multi-entity / phased rollout management
import React, { useEffect, useState } from "react";
import {
  Box, Paper, Button, Chip, Table, TableHead, TableRow, TableCell, TableBody,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem,
  IconButton, Tooltip, CircularProgress, Alert,
} from "@mui/material";
import { Plus, Trash2, Globe, ShieldCheck, Network } from "lucide-react";
import { toast } from "react-toastify";
import DashboardLayout from "../layouts/DashboardLayout";
import { entitiesApi } from "../api/extra";
import { formatApiError } from "../api/api";
import { fmtDateTime } from "../utils/format";

const STATUS_COLOR = { active: "#2E5A35", planned: "#8F5E14", retired: "#94A3B8" };
const PHASE_LABELS = { 1: "Phase 1 — Domestic Parent", 2: "Phase 2 — Domestic Subsidiaries", 3: "Phase 3 — International" };

export default function Entities() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", code: "", type: "subsidiary", jurisdiction: "IN",
    parent_entity_code: "", phase: 2, status: "planned", rollout_date: "",
    regulator: "", reporting_currency: "INR", notes: "",
  });

  const load = async () => {
    setLoading(true);
    try { const { data } = await entitiesApi.list(); setList(data.entities || []); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await entitiesApi.create(form);
      toast.success("Entity added");
      setOpen(false);
      setForm({ name: "", code: "", type: "subsidiary", jurisdiction: "IN", parent_entity_code: "IN-PARENT",
        phase: 2, status: "planned", rollout_date: "", regulator: "", reporting_currency: "INR", notes: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateStatus = async (e, status) => {
    try { await entitiesApi.update(e.entity_id, { status }); toast.success(`Marked ${status}`); load(); }
    catch (er) { toast.error(formatApiError(er)); }
  };

  const remove = async (e) => {
    if (!window.confirm(`Delete entity ${e.name}?`)) return;
    try { await entitiesApi.remove(e.entity_id); toast.success("Deleted"); load(); }
    catch (er) { toast.error(formatApiError(er)); }
  };

  const phases = [1, 2, 3];

  return (
    <DashboardLayout
      title="Entity & Rollout"
      subtitle="DOMESTIC · SUBSIDIARIES · INTERNATIONAL · PHASED"
      headerExtra={
        <Button size="small" variant="contained" onClick={() => setOpen(true)} startIcon={<Plus size={13} />}
          data-testid="add-entity-btn"
          sx={{ background: "#1E2A38", "&:hover": { background: "#0F1721" } }}>
          Add entity
        </Button>
      }
    >
      <Alert severity="info" sx={{ mb: 2 }}>
        Climate-risk capabilities roll out across the group in phases. Each entity has its own
        regulator and reporting currency; borrowers, collaterals and bank facilities can be tagged to
        an entity for consolidated, sub-consolidated and stand-alone reporting.
      </Alert>

      {loading ? <Box sx={{ py: 8, textAlign: "center" }}><CircularProgress size={24} /></Box> : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {phases.map((ph) => {
            const items = list.filter((e) => e.phase === ph);
            if (!items.length) return null;
            return (
              <Paper key={ph} sx={{ overflow: "hidden" }} data-testid={`phase-${ph}`}>
                <Box sx={{ p: 2, borderBottom: "1px solid #F0EFEA", display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: ph === 1 ? "#E8F0E9" : ph === 2 ? "#FFF4E5" : "#F0EFEA" }}>
                  <Box>
                    <Box className="cb-display" sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}>
                      {PHASE_LABELS[ph]}
                    </Box>
                    <Box sx={{ fontSize: 11.5, color: "#6B7280" }}>{items.length} entity / entities</Box>
                  </Box>
                  <Chip size="small" label={`Phase ${ph}`}
                    sx={{ background: ph === 1 ? "#2E5A35" : ph === 2 ? "#8F5E14" : "#1E2A38", color: "#FFFFFF", fontFamily: "IBM Plex Mono, monospace", fontWeight: 700 }} />
                </Box>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Entity</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Jurisdiction</TableCell>
                      <TableCell>Regulator</TableCell>
                      <TableCell>Currency</TableCell>
                      <TableCell>Rollout</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((e) => (
                      <TableRow key={e.entity_id} hover data-testid={`entity-row-${e.code}`}>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8 }}>
                            {e.type === "parent" ? <ShieldCheck size={14} color="#2E5A35" /> :
                              e.type === "subsidiary" ? <Network size={14} color="#1E2A38" /> :
                              <Globe size={14} color="#8F5E14" />}
                            <Box>
                              <Box sx={{ fontWeight: 600, fontSize: 13 }}>{e.name}</Box>
                              <Box className="cb-mono" sx={{ fontSize: 10.5, color: "#6B7280" }}>{e.code}</Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ textTransform: "capitalize" }}>{e.type?.replace("_", " ")}</TableCell>
                        <TableCell className="cb-mono">{e.jurisdiction}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{e.regulator || "—"}</TableCell>
                        <TableCell className="cb-mono">{e.reporting_currency || "—"}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{e.rollout_date || "—"}</TableCell>
                        <TableCell>
                          <TextField size="small" select value={e.status}
                            onChange={(ev) => updateStatus(e, ev.target.value)}
                            sx={{ minWidth: 120, "& .MuiOutlinedInput-root": { fontSize: 11.5 } }}
                            SelectProps={{ inputProps: { "data-testid": `entity-status-${e.code}` } }}>
                            {["planned", "active", "retired"].map((s) => (
                              <MenuItem key={s} value={s}>
                                <Chip size="small" label={s}
                                  sx={{ background: STATUS_COLOR[s] + "22", color: STATUS_COLOR[s], fontWeight: 600, fontSize: 9.5 }} />
                              </MenuItem>
                            ))}
                          </TextField>
                        </TableCell>
                        <TableCell align="right">
                          {e.type !== "parent" && (
                            <Tooltip title="Delete">
                              <IconButton size="small" color="error" onClick={() => remove(e)} data-testid={`entity-del-${e.code}`}>
                                <Trash2 size={13} />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Add entity dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontFamily: "Playfair Display, serif", fontWeight: 600 }}>Add entity</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gap: 1.6, gridTemplateColumns: "1fr 1fr", mt: 1 }}>
            <TextField size="small" required label="Name" value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              inputProps={{ "data-testid": "ef-name" }} sx={{ gridColumn: "1 / -1" }} />
            <TextField size="small" required label="Code (e.g. UK-SUB)" value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              inputProps={{ "data-testid": "ef-code" }} />
            <TextField size="small" select label="Type" value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}>
              {["subsidiary", "branch", "representative_office"].map((t) =>
                <MenuItem key={t} value={t}>{t.replace("_", " ")}</MenuItem>)}
            </TextField>
            <TextField size="small" label="Jurisdiction (ISO-2)" value={form.jurisdiction}
              onChange={(e) => setForm({ ...form, jurisdiction: e.target.value.toUpperCase() })} />
            <TextField size="small" label="Parent code" value={form.parent_entity_code}
              onChange={(e) => setForm({ ...form, parent_entity_code: e.target.value.toUpperCase() })} />
            <TextField size="small" select label="Phase" value={form.phase}
              onChange={(e) => setForm({ ...form, phase: Number(e.target.value) })}>
              <MenuItem value={1}>Phase 1</MenuItem>
              <MenuItem value={2}>Phase 2</MenuItem>
              <MenuItem value={3}>Phase 3</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Rollout date" value={form.rollout_date}
              onChange={(e) => setForm({ ...form, rollout_date: e.target.value })}
              InputLabelProps={{ shrink: true }} />
            <TextField size="small" label="Regulator" value={form.regulator}
              onChange={(e) => setForm({ ...form, regulator: e.target.value })} />
            <TextField size="small" label="Reporting currency" value={form.reporting_currency}
              onChange={(e) => setForm({ ...form, reporting_currency: e.target.value })} />
            <TextField size="small" multiline minRows={2} label="Notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              sx={{ gridColumn: "1 / -1" }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!form.name || !form.code}
            data-testid="ef-save"
            sx={{ background: "#1E2A38" }}>Add</Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}
